import { useState } from 'react'
import { useTheme } from '../context/ThemeContext'

function ComboWellSelector({ combos = [], selectedCombo, selectedWell, onComboChange, onWellChange }) {
    const { theme } = useTheme()
    const [search, setSearch] = useState('')
    const [expandedCombos, setExpandedCombos] = useState({})

    const filtered = combos.filter(c =>
        c.comboCurveTie.toLowerCase().includes(search.toLowerCase()) ||
        c.wells.some(w => w.wolfpakWellName.toLowerCase().includes(search.toLowerCase()))
    )

    const toggleExpand = (combo) => {
        setExpandedCombos(prev => ({ ...prev, [combo]: !prev[combo] }))
    }

    const handleComboClick = (combo) => {
        onComboChange(combo)
        onWellChange(null)
        setExpandedCombos(prev => ({ ...prev, [combo]: !prev[combo] }))
    }

    const handleWellClick = (combo, well) => {
        onComboChange(combo)
        onWellChange(well)
    }

    const isDark = theme === 'dark'

    return (
        <div className={`rounded-xl border-2 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-300'} h-full flex flex-col`}>
            <div className={`p-4 border-b-2 ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
                <h3 className={`text-lg font-bold mb-3 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                    Lease / Well
                </h3>
                <input
                    type="text"
                    placeholder="Search leases or wells..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className={`w-full px-4 py-3 text-base rounded-xl border-2 ${
                        isDark
                            ? 'bg-slate-800 border-slate-600 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                            : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                    } outline-none transition-colors`}
                />
            </div>
            <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                {filtered.map(combo => {
                    const isSelected = selectedCombo === combo.comboCurveTie
                    const isExpanded = expandedCombos[combo.comboCurveTie]

                    return (
                        <div key={combo.comboCurveTie} className="mb-1.5">
                            <button
                                onClick={() => handleComboClick(combo.comboCurveTie)}
                                className={`w-full text-left px-4 py-4 text-base rounded-xl flex items-center gap-3 transition-all border-2 ${
                                    isSelected && !selectedWell
                                        ? 'bg-blue-600 text-white font-bold border-blue-500 shadow-md'
                                        : isSelected
                                            ? isDark
                                                ? 'bg-slate-800 text-blue-300 font-semibold border-slate-600'
                                                : 'bg-blue-50 text-blue-700 font-semibold border-blue-200'
                                            : isDark
                                                ? 'text-gray-200 border-transparent hover:bg-slate-800 hover:border-slate-600 hover:text-white'
                                                : 'text-gray-700 border-transparent hover:bg-gray-100 hover:border-gray-300'
                                }`}
                            >
                                <span className={`text-sm transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                                    onClick={e => { e.stopPropagation(); toggleExpand(combo.comboCurveTie) }}>
                                    &#9654;
                                </span>
                                <span className="truncate leading-tight">{combo.comboCurveTie}</span>
                                <span className={`ml-auto text-sm font-bold flex-shrink-0 px-2 py-0.5 rounded-full ${
                                    isSelected && !selectedWell
                                        ? 'bg-blue-500 text-white'
                                        : isDark ? 'bg-slate-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                                }`}>
                                    {combo.wells.length}
                                </span>
                            </button>

                            {isExpanded && (
                                <div className="ml-6 mt-1.5 space-y-1 border-l-2 pl-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}">
                                    {combo.wells.map(well => {
                                        const isWellSelected = isSelected && selectedWell === well.wolfpakWellName
                                        const oil = well.todayOil
                                        const water = well.todayWater
                                        const fmt = (v) => v == null ? '--' : v.toLocaleString()
                                        return (
                                            <button
                                                key={well.wolfpakWellName}
                                                onClick={() => handleWellClick(combo.comboCurveTie, well.wolfpakWellName)}
                                                className={`w-full text-left px-4 py-3 text-sm rounded-xl transition-all border-2 flex items-center gap-2 ${
                                                    isWellSelected
                                                        ? 'bg-blue-600 text-white font-bold border-blue-500 shadow-md'
                                                        : isDark
                                                            ? 'text-gray-300 border-transparent hover:bg-slate-800 hover:border-slate-600 hover:text-gray-100'
                                                            : 'text-gray-600 border-transparent hover:bg-gray-100 hover:border-gray-300 hover:text-gray-900'
                                                }`}
                                                title={`Today — Oil: ${fmt(oil)} BBL | Water: ${fmt(water)} BBL`}
                                            >
                                                <span className="truncate flex-1 min-w-0">{well.wolfpakWellName}</span>
                                                <span className="flex items-center gap-1 flex-shrink-0 font-mono text-[11px] leading-none">
                                                    <span className={`px-1.5 py-0.5 rounded ${
                                                        isWellSelected
                                                            ? 'bg-green-500/30 text-green-100'
                                                            : isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                                                    }`}>
                                                        {fmt(oil)}
                                                    </span>
                                                    <span className={`px-1.5 py-0.5 rounded ${
                                                        isWellSelected
                                                            ? 'bg-blue-400/30 text-blue-100'
                                                            : isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {fmt(water)}
                                                    </span>
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
                {filtered.length === 0 && (
                    <p className={`text-base p-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No matches</p>
                )}
            </div>
        </div>
    )
}

export default ComboWellSelector
